import type { StrengthData, CardTemplate } from "./types";
import { domainColor } from "./styleHelpers";
import { getRowStyle, getRankStyle } from "./styleHelpers";
import { DOMAIN_LABELS_AR } from "./constants";

interface StrengthRowProps {
  s: StrengthData;
  template: CardTemplate;
  isRTL: boolean;
}

/**
 * Single strength row for Instagram (1080×1080) cards.
 * The parent card element sets the overall `direction`, so we only need to
 * align the text field internally.
 */
export function InstagramStrengthRow({ s, template, isRTL }: StrengthRowProps) {
  const color  = domainColor(s.domain);
  const name   = isRTL ? (s.nameAr ?? s.name) : s.name;
  const domain = isRTL ? (DOMAIN_LABELS_AR[s.domain] ?? s.domain) : s.domain;

  return (
    <div style={getRowStyle(template, color)}>
      <div style={{ ...getRankStyle(template, color, 36), fontFamily: "sans-serif" }}>
        {s.rank}
      </div>

      {template === "bold" && (
        <div style={{ width: 4, height: 28, borderRadius: 3, background: color, flexShrink: 0 }} />
      )}

      <div
        style={{
          flex: 1,
          fontSize: template === "minimal" ? 17 : 19,
          fontWeight: template === "minimal" ? 400 : 700,
          textAlign: isRTL ? "right" : "left",
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
        }}
      >
        {name}
        {template === "classic" && (
          <div style={{ fontSize: 11, color, fontWeight: 600, marginTop: 2 }}>{domain}</div>
        )}
      </div>

      {template === "bold" && (
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            padding: "4px 10px",
            borderRadius: 20,
            background: `${color}20`,
            color,
            border: `1px solid ${color}50`,
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          {domain}
        </div>
      )}
    </div>
  );
}

/**
 * Single strength row for LinkedIn (1200×630) cards.
 * Each row carries its own `direction` so columns work correctly in both LTR/RTL.
 */
export function LinkedInStrengthRow({ s, template, isRTL }: StrengthRowProps) {
  const color  = domainColor(s.domain);
  const name   = isRTL ? (s.nameAr ?? s.name) : s.name;
  const domain = isRTL ? (DOMAIN_LABELS_AR[s.domain] ?? s.domain) : s.domain;

  return (
    <div style={{ ...getRowStyle(template, color, true), direction: isRTL ? "rtl" : "ltr" }}>
      <div style={{ ...getRankStyle(template, color, 32), fontFamily: "sans-serif" }}>
        {s.rank}
      </div>

      {template === "bold" && (
        <div style={{ width: 3, height: 28, borderRadius: 2, background: color, flexShrink: 0 }} />
      )}

      <div style={{ flex: 1, overflow: "hidden", textAlign: isRTL ? "right" : "left" }}>
        <div
          style={{
            fontSize: template === "minimal" ? 13 : 14,
            fontWeight: template === "minimal" ? 400 : 700,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {name}
        </div>
        <div style={{ fontSize: 10, color, fontWeight: 600, marginTop: 2 }}>{domain}</div>
      </div>
    </div>
  );
}
